import { Component, OnInit, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ApiService } from 'src/app/shared/services/api.service';
import { ToastrMsgService } from 'src/app/shared/services/shared-services/toastr.service';

@Component({
  selector: 'app-document-upload',
  templateUrl: './document-upload.component.html',
  styleUrls: ['./document-upload.component.css']
})
export class DocumentUploadComponent implements OnInit {
  selectedFile: File | null = null;
  documentName = '';
  description = '';
  categoryId: number | null = null;
  projectId: number | null = null;
  /** When opening from `/document-list/:id`, project is fixed and shown as text. */
  projectLocked = false;
  /** Display name for locked project (from parent or API). */
  projectDisplayName = '';
  projectsForSelect: any[] = [];
  projectsLoading = false;
  tags: string[] = [];
  tagInput = '';
  uploading = false;

  categories: any[] = [];

  constructor(
    public dialogRef: MatDialogRef<DocumentUploadComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private apiService: ApiService,
    private toastrService: ToastrMsgService
  ) {
    if (data?.categories) {
      this.categories = data.categories;
    }
    if (data?.projectId != null && data.projectId !== '') {
      const n = Number(data.projectId);
      if (Number.isFinite(n) && n > 0) {
        this.projectLocked = true;
        this.projectId = n;
        const name = data?.projectName;
        if (typeof name === 'string' && name.trim()) {
          this.projectDisplayName = name.trim();
        }
      }
    }
    if (!this.projectLocked && Array.isArray(data?.projects) && data.projects.length) {
      this.projectsForSelect = [...data.projects];
    }
  }

  ngOnInit() {
    this.loadCategories();
    if (this.projectLocked) {
      if (!this.projectDisplayName) {
        this.loadLockedProjectLabel();
      }
    } else {
      if (!this.projectsForSelect.length) {
        this.loadProjectsForDropdown();
      }
    }
  }

  get canSubmit(): boolean {
    if (!this.selectedFile || this.uploading) {
      return false;
    }
    return this.projectId != null && this.projectId > 0;
  }

  private loadLockedProjectLabel(): void {
    if (this.projectId == null) {
      return;
    }
    this.apiService.getProjectCommonId(String(this.projectId)).subscribe({
      next: (proj: any) => {
        if (proj?.project_name) {
          this.projectDisplayName = proj.project_name;
        }
      },
      error: () => {
        if (!this.projectDisplayName) {
          this.projectDisplayName = `Project #${this.projectId}`;
        }
      },
    });
  }

  private loadProjectsForDropdown(): void {
    this.projectsLoading = true;
    this.apiService.getProjectListForDropdown({ limit: 300 }).subscribe({
      next: (res: any) => {
        this.projectsLoading = false;
        this.projectsForSelect = res.success ? res.data || [] : [];
      },
      error: () => {
        this.projectsLoading = false;
        this.toastrService.error('Failed to load projects');
      },
    });
  }

  loadCategories() {
    this.apiService.getDocumentCategories().subscribe({
      next: (res: any) => {
        if (res.success) {
          this.categories = res.data;
        }
      },
      error: (err: any) => {
        console.error('Error loading categories:', err);
      }
    });
  }

  onFileSelected(event: any) {
    const file = event.target.files[0];
    if (file) {
      this.selectedFile = file;
      if (!this.documentName) {
        this.documentName = file.name;
      }
    }
  }

  addTag() {
    if (this.tagInput.trim() && !this.tags.includes(this.tagInput.trim())) {
      this.tags.push(this.tagInput.trim());
      this.tagInput = '';
    }
  }

  removeTag(tag: string) {
    this.tags = this.tags.filter(t => t !== tag);
  }

  upload() {
    if (!this.selectedFile) {
      this.toastrService.error('Please select a file');
      return;
    }
    if (this.projectId == null || this.projectId < 1) {
      this.toastrService.error('Please select a project');
      return;
    }

    this.uploading = true;

    const uploadData: any = {
      document_name: this.documentName || this.selectedFile.name,
      description: this.description,
      tags: this.tags
    };

    if (this.categoryId) {
      uploadData.category_id = this.categoryId;
    }

    if (this.projectId) {
      uploadData.project_id = this.projectId;
    }

    this.apiService.uploadDocument(this.selectedFile, uploadData).subscribe({
      next: (res: any) => {
        if (res.success) {
          this.toastrService.success('Document uploaded successfully');
          this.dialogRef.close(true);
        } else {
          this.toastrService.error('Failed to upload document');
        }
        this.uploading = false;
      },
      error: (err: any) => {
        console.error('Error uploading document:', err);
        this.toastrService.error(err.error?.error || 'Failed to upload document');
        this.uploading = false;
      }
    });
  }

  cancel() {
    this.dialogRef.close(false);
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  }
}


